import React from 'react';
import API, { ENV } from '../utils/const';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  fallback?: React.ReactNode;
}

class ErrorBoundary extends React.Component<React.PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ error, errorInfo });
    
    // Enhance error logging with more context
    const errorDetails = {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
      location: {
        href: window.location.href,
        pathname: window.location.pathname,
      },
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      // Add any relevant app state here
      // For example: redux state, current route, etc.
    };

    // Check if it's a cross-origin error
    const isCrossOriginError = 
      error.message.includes("Script error");

    if (isCrossOriginError) {
      console.warn(
        "Cross-origin error detected. Ensure your scripts have 'crossorigin' attribute " +
        "and the server sends appropriate CORS headers."
      );
    }

    logErrorToMyService(errorDetails);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="error-boundary-fallback">
          <h1>Something went wrong</h1>
          {ENV !== 'production' && (
            <div style={{ whiteSpace: 'pre-wrap' }}>
              <p>{this.state.error?.toString()}</p>
              <small>{this.state.errorInfo?.componentStack}</small>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

function logErrorToMyService(errorDetails: any): void {
  // Add retry logic for logging
  const retryLogging = async (retries = 3) => {
    try {
      // Replace with your actual logging service API call
      console.error('Logging to service:', errorDetails);
      
      // Example of sending to a logging service:
      await fetch(`${API}/log-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorDetails)
      });
    } catch (loggingError) {
      if (retries > 0) {
        setTimeout(() => retryLogging(retries - 1), 1000);
      }
    }
  };

  retryLogging();
}

export default ErrorBoundary;